import LockIcon from "@mui/icons-material/Lock";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import {Alert, AlertTitle, Box, Button, Collapse, Grid, LinearProgress, Paper, Stack} from "@mui/material";
import {ErrorAlert, TextField, useFlag, View, ViewHeader} from "@variocube/app-ui";
import React, {type FormEvent, useState} from "react";
import {useAsyncCallback} from "react-async-hook";
import {useSignupApi} from "../../api";
import {Typography} from "../../component/typography";
import {useLocalization} from "../../i18n";
import {useParam} from "../../utils/useParam";
import {usePasswordStrength} from "../../utils/usePasswordStrength";

export function OebbSignup() {
	const {t} = useLocalization();
	const {signupOebb} = useSignupApi();

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [phone, setPhone] = useState("");

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const [success, setSuccess] = useFlag(false);

	const email = useParam("email");

	const {level, percentage} = usePasswordStrength(password);

	function validatePassword() {
		if (percentage < 60) {
			return t("signup.weakPassword");
		}
	}

	function validatePasswordConfirm(value: string) {
		if (value != password) {
			return t("signup.passwordMismatch");
		}
	}

	const {loading, error, execute} = useAsyncCallback(async (e: FormEvent) => {
		e.preventDefault();

		await signupOebb({
			firstName,
			lastName,
			email,
			phone,
			password,
		});
		setSuccess();
	});

	return (
		<View maxWidth="sm">
			<ViewHeader
				title={t("signup.title")}
				subTitle={!success && t("signup.oebb.subTitle")}
			/>
			<Collapse in={!success}>
				<form onSubmit={execute}>
					<Stack spacing={2}>
						<Paper sx={{width: "100%"}} variant="outlined">
							<Typography
								sx={{
									py: 1,
									px: 2,
									width: "100%",
									backgroundColor: "#eaeaea",
									borderBottom: "1px solid #ddd",
								}}
								startAdornment={<PhoneAndroidIcon />}
							>
								{t("signup.contactInfo")}
							</Typography>

							<Box p={2}>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											required
											label={t("common.firstName")}
											value={firstName}
											onChange={setFirstName}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											required
											label={t("common.lastName")}
											value={lastName}
											onChange={setLastName}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											required
											label={t("common.email")}
											value={email}
											fullWidth
											disabled
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											label={t("signup.phone")}
											value={phone}
											onChange={setPhone}
											fullWidth
										/>
									</Grid>
								</Grid>
							</Box>
						</Paper>

						<Paper sx={{width: "100%"}} variant="outlined">
							<Typography
								sx={{
									py: 1,
									px: 2,
									width: "100%",
									backgroundColor: "#eaeaea",
									borderBottom: "1px solid #ddd",
								}}
								startAdornment={<LockIcon />}
							>
								{t("signup.password")}
							</Typography>

							<Box p={2}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="body2" gutterBottom>
											{t("signup.passwordHint")}
										</Typography>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											required
											fullWidth
											label={t("signup.password")}
											type="password"
											value={password}
											onChange={setPassword}
											validate={validatePassword}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											required
											fullWidth
											label={t("signup.confirmPassword")}
											type="password"
											value={confirmPassword}
											onChange={setConfirmPassword}
											validate={validatePasswordConfirm}
										/>
									</Grid>
									<Grid item xs={12}>
										<LinearProgress
											variant="determinate"
											value={percentage}
											color={percentage < 60
												? "error"
												: (percentage > 79 ? "success" : "warning")}
										/>
										{level !== undefined && (
											<Typography
												sx={{
													color: percentage < 60
														? "red"
														: (percentage > 79 ? "green" : "orange"),
												}}
											>
												<strong>{level}</strong>
											</Typography>
										)}
									</Grid>
								</Grid>
							</Box>
						</Paper>

						{error && <ErrorAlert error={error} />}

						<Button variant="contained" color="primary" type="submit" disabled={loading}>
							{t("actions.next")}
						</Button>
					</Stack>
				</form>
			</Collapse>

			<Collapse in={success}>
				<Stack spacing={2}>
					<Alert severity="success">
						<AlertTitle>{t("signup.success.title")}</AlertTitle>
						<Typography paragraph>{t("signup.oebb.success.message")}</Typography>
					</Alert>
					<Alert severity="info">
						<AlertTitle>{t("signup.confirmEmail.title")}</AlertTitle>
						<Typography paragraph>{t("signup.confirmEmail.message", {email})}</Typography>
						<Typography paragraph>{t("signup.confirmEmail.spamHint")}</Typography>
					</Alert>
					<Box>
						<Typography variant="h3" align="center">{t("signup.greeting.title")}</Typography>
						<Typography align="center">{t("signup.greeting.signature")}</Typography>
					</Box>
				</Stack>
			</Collapse>
		</View>
	);
}
